import * as React from 'react';
import type { TFunction } from 'i18next';
import { Space, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getContactTypeLabel } from 'utils/display-enum-label';
import {
  deleteExcelContactRequest,
  fetchAllExcelTransactionsRequest,
  fetchOrganisationContactsRequest,
} from 'store/organisation/actions';
import {
  getAllExcelTransactionsPage,
  getAssignedContactIdentifiersForOrganisation,
  getOrganisationViesContacts,
  getOrgLoading,
} from 'store/organisation/selectors';
import { Contact } from 'store/vat-checker/models';
import { Preference, PreferenceActions } from 'utils/preferences';
import { isContactAssignedToTransactions } from 'utils/contact-transaction-usage';
import { StyledCard } from 'components/Custom/card';
import { CardTitleLargeSpan } from 'components/Custom/spans';
import { ExportButton, ScopeButton } from '@monorepo/shared-lib';
import {
  CUSTOMISE_COLUMNS_ICON_FONT_SIZE,
  CustomiseColumnsIconButtonWithTooltip,
  customiseColumnsIconButtonStyle,
} from 'components/Custom/customise-columns-icon-button';
import { ScopeTable } from 'components/Custom/table';
import { ScopeTableFilters } from 'components/Custom/ScopeTableFilters';
import { applyFilters as applyFiltersPipeline } from 'filter-system';
import { ColumnCustomizationDrawer } from 'components/Custom/ColumnCustomizationDrawer';
import { RoundInput } from 'components/Custom/inputs';
import { StyledPopconfirm } from 'components/Custom/Popconfirm';
import { ScopeDropdownButton } from 'components/Custom/ScopeDropdownButton';
import { TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH } from './tax-rates-promotion-banner';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  SettingOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { turnDrawerOn, turnDrawerOff } from 'store/app/actions';
import { DrawerType } from 'store/app/enums';
import { getDrawerData, getDrawerOpen } from 'store/app/selectors';
import { DrawerData } from 'store/app/types';
import { EntityNarrowSidePanelChrome } from './entity-narrow-side-panel-chrome';
import { Provider } from 'store/api/enums';
import { ContactDetailsCard } from './ContactDetailsCard';
import { AddCustomContact } from './add-custom-contact';

const ENTITY_DETAILS_BREAKPOINT = 1500;
const ENTITY_DETAILS_HEIGHT_BREAKPOINT = 750;
const CONTACTS_VIEW_COLUMN_KEY = 'view';
const CONTACTS_ACTIONS_COLUMN_KEY = 'actions';

/** Narrow layout: shrink below max-content table + inset for fixed AvatarGroup. */
const CONTACTS_NARROW_MAIN_COLUMN_STYLE: React.CSSProperties = {
  width: '100%',
  minWidth: 850,
  marginRight: '24px',
};

const CONTACTS_NARROW_INNER_STYLE: React.CSSProperties = {
  padding: '0 40px',
  width: '100%',
  maxWidth: 1250,
  margin: '0 auto',
};

const displayOrDash = (value: string | null | undefined) => {
  const trimmed = (value ?? '').trim();
  if (!trimmed || trimmed === 'null') return '-';
  return trimmed;
};

const formatContactType = (record: Contact, t: TFunction) =>
  record.contactType
    ? getContactTypeLabel(record.contactType, t)
    : '-';

interface ContactsTabProps {
  organisation?: any;
  handlePreferences: (action: PreferenceActions, preference: Preference) => any;
}

type ContactsColumnsPersistConfig = {
  visibleColumns: string[];
  lockedColumns?: string[];
  columnOrder?: string[];
};

export const ContactsTab: React.FC<ContactsTabProps> = ({
  organisation,
  handlePreferences,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const drawerOpen = useSelector(getDrawerOpen);
  const drawerData = useSelector(getDrawerData);
  const contactsColumnsDrawerOpen =
    drawerOpen && drawerData?.type === DrawerType.CUSTOMIZE_COLUMNS;
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const contactsWideLayout =
    windowWidth > ENTITY_DETAILS_BREAKPOINT &&
    windowHeight > ENTITY_DETAILS_HEIGHT_BREAKPOINT;
  const [selectedContactKey, setSelectedContactKey] = React.useState<
    string | null
  >(null);
  const [showContactDetailsNarrowPanel, setShowContactDetailsNarrowPanel] =
    React.useState(false);

  React.useEffect(() => {
    setShowContactDetailsNarrowPanel(false);
  }, [contactsWideLayout]);
  const isExcelEntity =
    organisation?.providerName?.toLowerCase() === Provider.EXCEL?.toLowerCase();
  const isReceiptsEntity =
    organisation?.providerName?.toLowerCase() ===
    Provider.RECEIPTS?.toLowerCase();
  const isExcelOrReceiptsEntity = isExcelEntity || isReceiptsEntity;
  const connectionId = organisation?.connectionId ?? '';
  const organisationId = organisation?.uuid ?? '';
  const organisationViesContactsSlice = useSelector(
    getOrganisationViesContacts
  );
  const orgLoading = useSelector(getOrgLoading);
  const allExcelTransactionsPage = useSelector(getAllExcelTransactionsPage);
  const assignedContactIdentifiers = useSelector(
    getAssignedContactIdentifiersForOrganisation(organisationId)
  );

  const isContactUsedInTransactions = React.useCallback(
    (contact: Contact) =>
      isContactAssignedToTransactions(contact, assignedContactIdentifiers),
    [assignedContactIdentifiers]
  );

  const contacts = React.useMemo(() => {
    if (!connectionId?.trim() || !organisationViesContactsSlice)
      return undefined;
    if (organisationViesContactsSlice.connectionId !== connectionId) {
      return undefined;
    }
    return organisationViesContactsSlice.contacts;
  }, [connectionId, organisationViesContactsSlice]);

  const contactsLoadedForConnection =
    !!organisationViesContactsSlice &&
    organisationViesContactsSlice.connectionId === connectionId;

  React.useEffect(() => {
    if (!connectionId) return;
    if (contactsLoadedForConnection) return;
    dispatch(fetchOrganisationContactsRequest(connectionId));
  }, [dispatch, connectionId, contactsLoadedForConnection]);

  React.useEffect(() => {
    if (!organisationId || !isExcelOrReceiptsEntity) return;

    const loadedForOrg =
      allExcelTransactionsPage?.organisationId === organisationId;
    const loadedCount = loadedForOrg
      ? allExcelTransactionsPage?.transactions?.length ?? 0
      : 0;
    const total = loadedForOrg ? allExcelTransactionsPage?.total ?? 0 : 0;
    if (loadedForOrg && (total === 0 || loadedCount >= total)) return;

    const pageSize = Math.max(total || 100, 100);
    dispatch(fetchAllExcelTransactionsRequest(organisationId, 1, pageSize));
  }, [
    dispatch,
    organisationId,
    isExcelOrReceiptsEntity,
    allExcelTransactionsPage?.organisationId,
    allExcelTransactionsPage?.total,
    allExcelTransactionsPage?.transactions?.length,
  ]);

  React.useEffect(() => {
    setSelectedContactKey(null);
  }, [connectionId]);

  const dataSource = React.useMemo(() => {
    return (contacts ?? [])
      .filter((c) => c.isVisible !== false)
      .map((contact: Contact) => ({
        ...contact,
        key: contact.contactUuid || contact.providerContactId,
      }));
  }, [contacts]);

  const defaultSelectedKey = dataSource[0]?.key ?? null;
  const effectiveSelectedKey = selectedContactKey ?? defaultSelectedKey;

  const selectedContact = React.useMemo(
    () => dataSource.find((row) => row.key === effectiveSelectedKey) ?? null,
    [dataSource, effectiveSelectedKey]
  );

  const contactsLoading =
    !!connectionId && (!contactsLoadedForConnection || orgLoading);

  const handleViewContactDetails = React.useCallback(
    (record: Contact & { key: string }) => {
      setSelectedContactKey(record.key);
      if (
        windowWidth <= ENTITY_DETAILS_BREAKPOINT ||
        windowHeight <= ENTITY_DETAILS_HEIGHT_BREAKPOINT
      ) {
        setShowContactDetailsNarrowPanel(true);
      }
    },
    [windowWidth, windowHeight]
  );

  const contactColumnKeys = React.useMemo(
    () => [
      'name',
      'email',
      'phone',
      'taxNumber',
      'contactType',
      'country',
      'address',
      'address1',
      'zipCode',
      'mainContact',
      'mainContactEmail',
      'otherContact',
      'otherContactEmail',
    ],
    []
  );

  const defaultVisibleColumns = React.useMemo(
    () => ['name', 'email', 'phone', 'taxNumber', 'contactType'],
    []
  );

  const contactsColumnsPrefKey =
    organisationId?.trim() && `contacts-tab-columns-config-${organisationId}`;

  const [visibleColumns, setVisibleColumns] = React.useState<string[] | null>(
    null
  );
  const [lockedColumns, setLockedColumns] = React.useState<string[]>([]);
  const [columnOrder, setColumnOrder] = React.useState<string[] | undefined>(
    undefined
  );

  React.useEffect(() => {
    if (!contactsColumnsPrefKey) {
      setVisibleColumns(null);
      setLockedColumns([]);
      setColumnOrder(undefined);
      return;
    }

    const raw = handlePreferences(PreferenceActions.GET, {
      name: contactsColumnsPrefKey,
    }) as ContactsColumnsPersistConfig | undefined;

    if (raw?.visibleColumns?.length) {
      const validKeys = new Set(contactColumnKeys);
      setVisibleColumns(raw.visibleColumns.filter((k) => validKeys.has(k)));
      setLockedColumns(
        (raw.lockedColumns ?? []).filter((k) => validKeys.has(k))
      );
      const ordered = (raw.columnOrder ?? []).filter((k) => validKeys.has(k));
      setColumnOrder(ordered.length > 0 ? ordered : undefined);
    } else {
      setVisibleColumns(null);
      setLockedColumns([]);
      setColumnOrder(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once when org changes
  }, [contactsColumnsPrefKey]);

  const handleColumnsChange = React.useCallback(
    (config: {
      visibleColumns: string[];
      lockedColumns: string[];
      columnOrder: string[];
    }) => {
      const validKeys = new Set(contactColumnKeys);
      const sanitized: ContactsColumnsPersistConfig = {
        visibleColumns: config.visibleColumns.filter((k) => validKeys.has(k)),
        lockedColumns: (config.lockedColumns ?? []).filter((k) =>
          validKeys.has(k)
        ),
        columnOrder: (config.columnOrder ?? []).filter((k) => validKeys.has(k)),
      };

      setVisibleColumns(sanitized.visibleColumns);
      setLockedColumns(sanitized.lockedColumns ?? []);
      setColumnOrder(
        sanitized.columnOrder && sanitized.columnOrder.length > 0
          ? sanitized.columnOrder
          : undefined
      );

      if (contactsColumnsPrefKey) {
        handlePreferences(PreferenceActions.SET, {
          name: contactsColumnsPrefKey,
          value: sanitized,
        });
      }
    },
    [contactColumnKeys, contactsColumnsPrefKey, handlePreferences]
  );

  const effectiveVisibleColumns = React.useMemo(() => {
    const base = visibleColumns ?? defaultVisibleColumns;
    const validKeys = new Set(contactColumnKeys);
    const deduped = Array.from(new Set(base.filter((k) => validKeys.has(k))));
    let columns = deduped.length > 0 ? deduped : defaultVisibleColumns;
    if (!columns.includes(CONTACTS_VIEW_COLUMN_KEY)) {
      columns = [...columns, CONTACTS_VIEW_COLUMN_KEY];
    }
    if (!columns.includes(CONTACTS_ACTIONS_COLUMN_KEY)) {
      columns = [...columns, CONTACTS_ACTIONS_COLUMN_KEY];
    }
    return columns;
  }, [
    visibleColumns,
    contactColumnKeys,
    defaultVisibleColumns,
  ]);

  const getContactColumnTitle = React.useCallback(
    (key: string) => {
      switch (key) {
        case 'name':
          return t('common.name');
        case 'email':
          return t('signup.email-address');
        case 'phone':
          return t('contacts.phone');
        case 'taxNumber':
          return t('main.tax-number');
        case 'contactType':
          return t('contacts.contactType');
        case 'country':
          return t('common.country');
        case 'address':
          return t('organisation.registered-address');
        case 'address1':
          return t('signup.address-line1');
        case 'zipCode':
          return t('signup.zip-code');
        case 'mainContact':
          return t('vat-checker.primary-contact');
        case 'mainContactEmail':
          return t('vat-checker.primary-contact-email');
        case 'otherContact':
          return t('vat-checker.secondary-contact');
        case 'otherContactEmail':
          return t('vat-checker.secondary-contact-email');
        default:
          return key;
      }
    },
    [t]
  );

  const openContactsColumnsDrawer = React.useCallback(() => {
    const customizableColumns = contactColumnKeys.map((key) => ({
      key,
      title: getContactColumnTitle(key),
    }));

    const drawerData: DrawerData = {
      title: t('main.columns', 'Columns'),
      type: DrawerType.CUSTOMIZE_COLUMNS,
      component: (
        <ColumnCustomizationDrawer
          columns={customizableColumns}
          visibleColumns={effectiveVisibleColumns}
          lockedColumns={lockedColumns}
          columnOrder={columnOrder}
          onSave={handleColumnsChange}
          onClose={() => dispatch(turnDrawerOff())}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  }, [
    contactColumnKeys,
    columnOrder,
    dispatch,
    effectiveVisibleColumns,
    getContactColumnTitle,
    handleColumnsChange,
    lockedColumns,
    t,
  ]);

  const contactsCustomiseColumnTitle = React.useMemo(
    () => (
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          width: '100%',
          minHeight: 32,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            justifyContent: 'flex-end',
          }}
        >
          <CustomiseColumnsIconButtonWithTooltip
            type="default"
            disabled={dataSource.length === 0}
            icon={
              <SettingOutlined
                style={{ fontSize: CUSTOMISE_COLUMNS_ICON_FONT_SIZE }}
              />
            }
            style={customiseColumnsIconButtonStyle(contactsColumnsDrawerOpen)}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              openContactsColumnsDrawer();
            }}
          />
        </div>
      </div>
    ),
    [
      contactsColumnsDrawerOpen,
      dataSource.length,
      openContactsColumnsDrawer,
      t,
    ]
  );

  const openContactDrawer = React.useCallback(
    (contact?: Contact | null) => {
      if (!organisationId) return;
      const isEdit = !!(contact?.contactUuid || contact?.providerContactId);
      const drawerData: DrawerData = {
        title: isEdit ? t('organisation.edit-contact') : t('organisation.add-contact'),
        type: DrawerType.CREATE_TAX_TEMPLATE,
        component: (
          <AddCustomContact organisationId={organisationId} contact={contact} />
        ),
      };
      dispatch(turnDrawerOn(drawerData));
    },
    [organisationId, dispatch]
  );

  // const onDeleteContact = React.useCallback(
  //   (record: Contact & { key: string }) => {
  //     if (!organisationId || !isExcelOrReceiptsEntity) return;
  //     const contactUuid = getContactId(record);
  //     if (!contactUuid) return;
  //     if (record.key === effectiveSelectedKey) {
  //       setSelectedContactKey(null);
  //     }
  //     dispatch(deleteExcelContactRequest(organisationId, contactUuid));
  //   },
  //   [
  //     organisationId,
  //     dispatch,
  //     effectiveSelectedKey,
  //     isExcelOrReceiptsEntity,
  //   ]
  // );
  const onDeleteContact = React.useCallback(
    (record: Contact) => {
      if (!organisationId || !record.contactUuid) return;
      if (!isExcelOrReceiptsEntity) return;
      dispatch(deleteExcelContactRequest(organisationId, record.contactUuid));
    },
    [dispatch, organisationId, isExcelOrReceiptsEntity]
  );

  const columns = React.useMemo(() => {
    const baseColumns = [
      {
        title: t('common.name'),
        dataIndex: 'name',
        key: 'name',
        render: (value: string) => displayOrDash(value),
      },
      {
        title: t('signup.email-address'),
        dataIndex: 'email',
        key: 'email',
        render: (value: string) => displayOrDash(value),
      },
      {
        title: t('contacts.phone'),
        dataIndex: 'phone',
        key: 'phone',
        render: (value: string) => displayOrDash(value),
      },
      {
        title: t('main.tax-number'),
        dataIndex: 'taxNumber',
        key: 'taxNumber',
        render: (value: string) => displayOrDash(value),
      },
      {
        title: t('contacts.contactType'),
        dataIndex: 'contactType',
        key: 'contactType',
        render: (_: unknown, record: Contact) => formatContactType(record, t),
      },
      {
        title: t('common.country'),
        dataIndex: 'country',
        key: 'country',
        render: (value: string) => displayOrDash(value),
      },
      {
        title: t('organisation.registered-address'),
        dataIndex: 'address',
        key: 'address',
        render: (value: string) => displayOrDash(value),
      },
      {
        title: t('signup.address-line1'),
        dataIndex: 'address1',
        key: 'address1',
        render: (value: string) => displayOrDash(value),
      },
      {
        title: t('signup.zip-code'),
        dataIndex: 'zipCode',
        key: 'zipCode',
        render: (value: string) => displayOrDash(value),
      },
      {
        title: t('vat-checker.primary-contact'),
        dataIndex: 'mainContact',
        key: 'mainContact',
        render: (value: string) => displayOrDash(value),
      },
      {
        title: t('vat-checker.primary-contact-email'),
        dataIndex: 'mainContactEmail',
        key: 'mainContactEmail',
        render: (value: string) => displayOrDash(value),
      },
      {
        title: t('vat-checker.secondary-contact'),
        dataIndex: 'otherContact',
        key: 'otherContact',
        render: (value: string) => displayOrDash(value),
      },
      {
        title: t('vat-checker.secondary-contact-email'),
        dataIndex: 'otherContactEmail',
        key: 'otherContactEmail',
        render: (value: string) => displayOrDash(value),
      },
    ];

    const actionsColumn = {
      title: contactsCustomiseColumnTitle,
      key: CONTACTS_ACTIONS_COLUMN_KEY,
      width: isExcelOrReceiptsEntity ? 120 : 52,
      fixed: 'right' as const,
      align: 'right' as const,
      render: isExcelOrReceiptsEntity
        ? (_: unknown, record: Contact & { key: string }) => {
            const contactIsUsedInTransactions =
              isContactUsedInTransactions(record);
            return (
              <div onClick={(e) => e.stopPropagation()}>
                <ScopeDropdownButton
                  label={t('common.edit')}
                  menu={{
                    items: [
                      {
                        key: 'edit',
                        label: t('organisation.edit-contact'),
                        icon: <EditOutlined style={{ fontSize: 16 }} />,
                        onClick: () => openContactDrawer(record),
                      },
                      {
                        key: 'delete',
                        label: (
                          <Tooltip
                            title={
                              contactIsUsedInTransactions
                                ? t(
                                    'notifications.this-contact-is-assigned-to-one-or-more-transactions-remove-it-f'
                                  )
                                : undefined
                            }
                          >
                            <span>
                              <StyledPopconfirm
                                title={t('organisation.delete-this-contact-title')}
                                onConfirm={() => onDeleteContact(record)}
                                okText={t('common.delete')}
                                cancelText={t('common.cancel')}
                                placement="left"
                                disabled={contactIsUsedInTransactions}
                              >
                                <span
                                  style={
                                    contactIsUsedInTransactions
                                      ? {
                                          color: 'rgba(0,0,0,0.25)',
                                          cursor: 'not-allowed',
                                        }
                                      : undefined
                                  }
                                >
                                  {t('organisation.delete-contact')}
                                </span>
                              </StyledPopconfirm>
                            </span>
                          </Tooltip>
                        ),
                        icon: <DeleteOutlined style={{ fontSize: 16 }} />,
                        danger: true,
                        disabled: contactIsUsedInTransactions,
                        onClick: () => {},
                      },
                    ],
                  }}
                  onClick={() => openContactDrawer(record)}
                  placement="bottomRight"
                />
              </div>
            );
          }
        : () => null,
    };

    return [...baseColumns, actionsColumn];
  }, [
    t,
    isExcelOrReceiptsEntity,
    contactsCustomiseColumnTitle,
    openContactDrawer,
    onDeleteContact,
    isContactUsedInTransactions,
  ]);

  const [searchText, setSearchText] = React.useState('');
  const [filtersState, setFiltersState] = React.useState<Record<string, any>>(
    {}
  );

  const pipelineFilteredDataSource = React.useMemo(() => {
    const base = dataSource ?? [];
    if (!filtersState || Object.keys(filtersState).length === 0) {
      return base;
    }
    return (
      applyFiltersPipeline(base, filtersState, { mainLocaleKey: 'contacts' }) ??
      base
    );
  }, [dataSource, filtersState]);

  const filteredDataSource = React.useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return pipelineFilteredDataSource;
    return (pipelineFilteredDataSource ?? []).filter((c) => {
      const name = (c.name ?? '').toString().toLowerCase();
      const email = (c.email ?? '').toString().toLowerCase();
      const phone = (c.phone ?? '').toString().toLowerCase();
      const taxNumber = (c.taxNumber ?? '').toString().toLowerCase();
      const contactType = formatContactType(c as Contact, t).toLowerCase();
      return (
        name.includes(q) ||
        email.includes(q) ||
        phone.includes(q) ||
        taxNumber.includes(q) ||
        contactType.includes(q)
      );
    });
  }, [pipelineFilteredDataSource, searchText]);

  const handleRefreshContacts = () => {
    if (!connectionId) return;
    dispatch(fetchOrganisationContactsRequest(connectionId));
  };

  const renderContactDetailsCard = (opts?: {
    inlineBesideMain?: boolean;
    onClose?: () => void;
  }) => {
    if (!selectedContact) return null;
    return (
      <ContactDetailsCard
        selectedContact={selectedContact}
        providerName={organisation?.providerName}
        shortCode={organisation?.shortCode}
        windowWidth={windowWidth}
        windowHeight={windowHeight}
        ENTITY_DETAILS_BREAKPOINT={ENTITY_DETAILS_BREAKPOINT}
        ENTITY_DETAILS_HEIGHT_BREAKPOINT={ENTITY_DETAILS_HEIGHT_BREAKPOINT}
        inlineBesideMain={opts?.inlineBesideMain}
        hasAttachments={false}
        onClose={opts?.onClose}
      />
    );
  };

  const contactsTableCard = (
    <StyledCard
      style={{
        width: '100%',
        maxWidth: '100%',
        minWidth: contactsWideLayout ? 600 : 0,
        ...(!contactsWideLayout ? { overflowX: 'auto' as const } : {}),
      }}
    >
      <div
        style={{
          padding: '5px',
          paddingLeft: '5px',
          paddingTop: '5px',
          minWidth: contactsWideLayout ? 500 : 0,
          margin: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            // gap: 12,
            marginBottom: 35,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              minHeight: '32px',
            }}
          >
            <CardTitleLargeSpan style={{ fontSize: 20, lineHeight: '32px' }}>
              {t('organisation.reference-contacts')}
            </CardTitleLargeSpan>
            <RoundInput
              prefix={
                <SearchOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
              }
              placeholder={t('organisation.search-contacts')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              width="260px"
              height="32px"
              style={{ fontSize: 13 }}
            />
          </div>
          <Space size={8}>
            <ExportButton
              type="default"
              onClick={handleRefreshContacts}
              disabled={!connectionId || contactsLoading}
            >
              <SyncOutlined style={{ marginRight: 6 }} />
              {t('vat-report.refresh-data')}
            </ExportButton>
            {isExcelOrReceiptsEntity && (
              <ScopeButton
                type="primary"
                width="fit-content"
                height="32px"
                fontSize="13px"
                lineheight="20px"
                onClick={() => openContactDrawer()}
                disabled={!connectionId}
              >
                <PlusOutlined style={{ marginRight: 6 }} />
                {t('organisation.add-contact')}
              </ScopeButton>
            )}
          </Space>
        </div>
          <ScopeTableFilters
            filters={['name', 'email', 'phone', 'taxNumber', 'contactType']}
            mainLocaleKey="contacts"
            value={filtersState}
            onChange={setFiltersState}
            originalDataSource={dataSource}
            tableDataSource={pipelineFilteredDataSource}
            embedded={false}
            filterRowMarginBottom={6}
          />
          <ScopeTable
            tableDataSource={filteredDataSource}
            originalDataSource={dataSource}
            columns={columns}
            rowKey="key"
            pagination={true}
            rowsPerPage={10}
            enableColumnsCustomization={true}
            hideToolbarColumnsCustomizationButton
            onColumnsChange={handleColumnsChange}
            visibleColumns={effectiveVisibleColumns}
            lockedColumns={lockedColumns}
            columnOrder={columnOrder}
            alwaysFixedColumns={[CONTACTS_ACTIONS_COLUMN_KEY]}
            transactionType=""
            reportId=""
            rowClassName={(record: Contact & { key: string }) =>
              record.key === effectiveSelectedKey
                ? 'ant-table-row-selected'
                : ''
            }
            onRow={(record: Contact & { key: string }) => ({
              style: { cursor: 'pointer' },
              onClick: () => handleViewContactDetails(record),
            })}
            rowHeight={40}
            showSizeChanger
            getNewPageSize={() => {}}
            scroll={{ x: 'max-content' }}
            fontSize={12}
            headerFontSize={12}
          />
      </div>
    </StyledCard>
  );

  return (
    <div
      className="entity-contacts-tab"
      style={{ minHeight: 'calc(100vh - 120px)' }}
    >
      <div
        className={`contacts-tab-wrapper${
          !contactsWideLayout ? ' contacts-tab-wrapper--narrow' : ''
        }`}
      >
        {contactsWideLayout ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              gap: 20,
              alignItems: 'flex-start',
            }}
          >
            <div style={{ flex: 1, minWidth: 600 }}>{contactsTableCard}</div>
            <div
              style={{
                width: TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH,
                flexShrink: 0,
              }}
            >
              {renderContactDetailsCard({ inlineBesideMain: true })}
            </div>
          </div>
        ) : (
          <div className="entity-overview-tab entity-details-narrow-side-shell">
            <div style={CONTACTS_NARROW_MAIN_COLUMN_STYLE}>
              <div style={CONTACTS_NARROW_INNER_STYLE}>{contactsTableCard}</div>
            </div>
            {showContactDetailsNarrowPanel && selectedContact ? (
              <div
                className="entity-narrow-side-panel"
                style={{
                  height: 'calc(100vh - 180px)',
                  position: 'fixed',
                  float: 'right',
                  right: 17,
                }}
              >
                {renderContactDetailsCard({
                  onClose: () => setShowContactDetailsNarrowPanel(false),
                })}
              </div>
            ) : (
              <EntityNarrowSidePanelChrome
                panelOpen={false}
                onOpenPanel={() => setShowContactDetailsNarrowPanel(true)}
                onClosePanel={() => setShowContactDetailsNarrowPanel(false)}
                tabLabel={t('organisation.contact-details-title')}
                panelWidthPx={TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH}
              >
                {null}
              </EntityNarrowSidePanelChrome>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

